import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client.js';
import { useTenant } from '../context/TenantContext.jsx';

function fmtDateTime(value) {
  if (!value) return '—';
  return String(value).replace('T', ' ').slice(0, 16);
}

function StatCard({ label, value, tone = 'default' }) {
  const toneCls =
    tone === 'warn'
      ? 'border-amber-300/60 bg-amber-50 dark:border-amber-400/30 dark:bg-amber-950/20'
      : tone === 'success'
      ? 'border-emerald-300/60 bg-emerald-50 dark:border-emerald-400/30 dark:bg-emerald-950/20'
      : 'border-ink/10 bg-white dark:border-white/10 dark:bg-white/5';
  return (
    <div className={`rounded-2xl border p-4 shadow-sm ${toneCls}`}>
      <p className="text-[11px] font-bold uppercase tracking-wider text-ink/50 dark:text-frost-muted">{label}</p>
      <p className="mt-1 text-2xl font-bold tabular-nums text-ink dark:text-mist">{value}</p>
    </div>
  );
}

const ALERT_BADGE = {
  pending: 'bg-amber-500/15 text-amber-800 dark:text-amber-200',
  notified: 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-200',
  cancelled: 'bg-zinc-500/15 text-zinc-600 dark:text-zinc-300',
};
const ALERT_LABEL = { pending: 'Aguardando', notified: 'Avisado', cancelled: 'Cancelado' };

/** Painel do dono: Radar de Clientes (histórico de acesso/pesquisa + alertas de reposição). */
export function CustomerRadarPage() {
  const { tenantId } = useTenant();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [savingCfg, setSavingCfg] = useState(false);
  const [retentionInput, setRetentionInput] = useState(60);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setErr('');
    try {
      const d = await api(`/tenants/${tenantId}/radar/overview`);
      setData(d);
      setRetentionInput(Number(d?.retention_days ?? 60));
    } catch (e) {
      setErr(e?.message || 'Não foi possível carregar o Radar.');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  async function saveConfig(patch) {
    if (!tenantId) return;
    setSavingCfg(true);
    try {
      const d = await api(`/tenants/${tenantId}/radar/config`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
      setData((prev) => (prev ? { ...prev, enabled: d.enabled, retention_days: d.retention_days } : prev));
      setRetentionInput(Number(d?.retention_days ?? 60));
    } catch (e) {
      window.alert(e?.message || 'Não foi possível guardar a configuração.');
    } finally {
      setSavingCfg(false);
    }
  }

  if (!tenantId) {
    return (
      <div className="py-12 text-center text-sm text-ink/60 dark:text-frost-muted">
        Selecione uma empresa em{' '}
        <Link className="font-semibold text-accent" to="/app/empresa">
          Empresas
        </Link>
        .
      </div>
    );
  }

  const counts = data?.counts || {};

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-xl font-bold tracking-tight text-ink dark:text-mist sm:text-2xl">
            Radar de Clientes
          </h1>
          <p className="mt-1 text-sm text-ink/60 dark:text-frost-muted">
            Acessos, pesquisas e avisos de reposição automáticos. Quando um cliente procura algo esgotado, avisamos por
            e-mail assim que o produto volta ao estoque.
          </p>
        </div>
        <button
          type="button"
          onClick={() => load()}
          disabled={loading}
          className="rounded-full border border-ink/15 px-4 py-2 text-xs font-semibold text-ink/70 hover:bg-ink/5 disabled:opacity-50 dark:border-white/15 dark:text-mist"
        >
          {loading ? 'A actualizar…' : 'Actualizar'}
        </button>
      </header>

      {err ? (
        <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
          {err}
        </p>
      ) : null}

      {/* Configuração */}
      <section className="rounded-2xl border border-ink/10 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-white/5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              className="h-5 w-5"
              checked={Boolean(data?.enabled)}
              disabled={savingCfg}
              onChange={(e) => saveConfig({ enabled: e.target.checked })}
            />
            <span className="text-sm font-semibold text-ink dark:text-mist">
              Ativar o Radar de Clientes nesta loja
            </span>
          </label>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-ink/60 dark:text-frost-muted">Guardar dados por</span>
            <input
              type="number"
              min={15}
              max={90}
              value={retentionInput}
              disabled={savingCfg}
              onChange={(e) => setRetentionInput(e.target.value)}
              className="w-20 rounded-xl border border-ink/15 bg-white px-2 py-1.5 text-center text-sm dark:border-white/15 dark:bg-white/5"
            />
            <span className="text-xs font-medium text-ink/60 dark:text-frost-muted">dias (15–90)</span>
            <button
              type="button"
              disabled={savingCfg}
              onClick={() => saveConfig({ retention_days: Math.max(15, Math.min(90, Number(retentionInput) || 60)) })}
              className="rounded-xl bg-accent px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
            >
              Guardar
            </button>
          </div>
        </div>
        {!data?.enabled ? (
          <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100">
            O Radar está desligado: nada é registado e nenhum aviso é enviado enquanto não ativar.
          </p>
        ) : null}
      </section>

      {/* Métricas */}
      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <StatCard label="Acessos" value={counts.access_total ?? 0} />
        <StatCard label="Pesquisas" value={counts.search_total ?? 0} />
        <StatCard label="Buscas sem estoque" value={counts.no_result_total ?? 0} tone="warn" />
        <StatCard label="Avisos pendentes" value={counts.alerts_pending ?? 0} tone="warn" />
        <StatCard label="Avisos enviados" value={counts.alerts_notified ?? 0} tone="success" />
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Oportunidades: buscas sem resultado */}
        <section className="rounded-2xl border border-ink/10 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-white/5">
          <h2 className="text-sm font-bold text-ink dark:text-mist">Oportunidades de catálogo</h2>
          <p className="mt-1 text-xs text-ink/60 dark:text-frost-muted">
            Termos mais pesquisados que não tinham estoque — candidatos a repor ou cadastrar.
          </p>
          <ul className="mt-3 space-y-1.5">
            {(data?.no_result_searches || []).length === 0 ? (
              <li className="rounded-xl border border-dashed border-ink/15 px-3 py-6 text-center text-sm text-ink/50 dark:border-white/15 dark:text-frost-muted">
                Sem buscas sem estoque registadas.
              </li>
            ) : (
              data.no_result_searches.map((s, i) => (
                <li
                  key={i}
                  className="flex items-center justify-between gap-3 rounded-xl border border-ink/10 px-3 py-2 text-sm dark:border-white/10"
                >
                  <span className="min-w-0 truncate font-medium text-ink dark:text-mist">{s.term}</span>
                  <span className="shrink-0 text-[11px] text-ink/50 dark:text-frost-muted">
                    {s.customers} cliente(s) · {s.count}×
                  </span>
                </li>
              ))
            )}
          </ul>
        </section>

        {/* Top buscas */}
        <section className="rounded-2xl border border-ink/10 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-white/5">
          <h2 className="text-sm font-bold text-ink dark:text-mist">Buscas mais frequentes</h2>
          <p className="mt-1 text-xs text-ink/60 dark:text-frost-muted">O que os clientes mais procuram nesta loja.</p>
          <ul className="mt-3 space-y-1.5">
            {(data?.top_searches || []).length === 0 ? (
              <li className="rounded-xl border border-dashed border-ink/15 px-3 py-6 text-center text-sm text-ink/50 dark:border-white/15 dark:text-frost-muted">
                Sem buscas registadas.
              </li>
            ) : (
              data.top_searches.map((s, i) => (
                <li
                  key={i}
                  className="flex items-center justify-between gap-3 rounded-xl border border-ink/10 px-3 py-2 text-sm dark:border-white/10"
                >
                  <span className="min-w-0 truncate font-medium text-ink dark:text-mist">{s.term}</span>
                  <span className="shrink-0 text-[11px] text-ink/50 dark:text-frost-muted">
                    {s.count}×{s.no_stock_count > 0 ? ` · ${s.no_stock_count} sem estoque` : ''}
                  </span>
                </li>
              ))
            )}
          </ul>
        </section>
      </div>

      {/* Avisos de reposição */}
      <section className="rounded-2xl border border-ink/10 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-white/5">
        <h2 className="text-sm font-bold text-ink dark:text-mist">Fila de avisos de reposição</h2>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead>
              <tr className="border-b border-ink/10 text-[11px] uppercase tracking-wide text-ink/50 dark:border-white/10 dark:text-frost-muted">
                <th className="px-2 py-2">Cliente</th>
                <th className="px-2 py-2">Procurou</th>
                <th className="px-2 py-2">Produto</th>
                <th className="px-2 py-2">Estado</th>
                <th className="px-2 py-2">Quando</th>
              </tr>
            </thead>
            <tbody>
              {(data?.alerts || []).length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-2 py-6 text-center text-ink/50 dark:text-frost-muted">
                    Nenhum aviso registado.
                  </td>
                </tr>
              ) : (
                data.alerts.map((a) => (
                  <tr key={a.id} className="border-b border-ink/5 dark:border-white/5">
                    <td className="px-2 py-2">
                      <p className="font-medium text-ink dark:text-mist">{a.cliente_nome}</p>
                      <p className="text-[11px] text-ink/50 dark:text-frost-muted">{a.cliente_email}</p>
                    </td>
                    <td className="px-2 py-2 text-ink/70 dark:text-frost-muted">{a.term}</td>
                    <td className="px-2 py-2 text-ink/70 dark:text-frost-muted">{a.product_nome || '—'}</td>
                    <td className="px-2 py-2">
                      <span
                        className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${ALERT_BADGE[a.status] || ALERT_BADGE.pending}`}
                      >
                        {ALERT_LABEL[a.status] || a.status}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-[11px] text-ink/50 dark:text-frost-muted">
                      {a.notified_at ? fmtDateTime(a.notified_at) : fmtDateTime(a.created_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Acessos recentes */}
      <section className="rounded-2xl border border-ink/10 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-white/5">
        <h2 className="text-sm font-bold text-ink dark:text-mist">Acessos recentes</h2>
        <ul className="mt-3 grid gap-1.5 sm:grid-cols-2">
          {(data?.recent_access || []).length === 0 ? (
            <li className="rounded-xl border border-dashed border-ink/15 px-3 py-6 text-center text-sm text-ink/50 dark:border-white/15 dark:text-frost-muted">
              Sem acessos registados.
            </li>
          ) : (
            data.recent_access.map((a, i) => (
              <li
                key={i}
                className="flex items-center justify-between gap-3 rounded-xl border border-ink/10 px-3 py-2 text-sm dark:border-white/10"
              >
                <span className="min-w-0 truncate text-ink dark:text-mist">{a.cliente_nome}</span>
                <span className="shrink-0 text-[11px] tabular-nums text-ink/50 dark:text-frost-muted">
                  {fmtDateTime(a.at)}
                </span>
              </li>
            ))
          )}
        </ul>
      </section>
    </div>
  );
}
